import React from 'react';
import { ManualMaterialItem } from '../../types/bidding';
import { formatCurrency } from '../../utils/bidCalculator';
import './BucketEditor.css';

interface ManualMaterialsEditorProps {
  items: ManualMaterialItem[];
  onUpdate: (items: ManualMaterialItem[]) => void;
  readOnly?: boolean;
}

// Common materials with default pricing
const COMMON_MATERIALS = [
  { description: 'Timberlinx A475 Post Bases', unit: 'ea', unitPrice: 80 },
  { description: 'Pegs', unit: 'ea', unitPrice: 1.5 },
  { description: 'Fasteners (SIP screws, lags, etc.)', unit: 'lot', unitPrice: 0 },
  { description: 'Timber Bolts', unit: 'ea', unitPrice: 5 },
  { description: 'Through Bolts', unit: 'ea', unitPrice: 3 },
  { description: 'Anchor Bolts', unit: 'ea', unitPrice: 8 },
  { description: 'Steel Connectors', unit: 'ea', unitPrice: 25 },
];

const ManualMaterialsEditor: React.FC<ManualMaterialsEditorProps> = ({
  items,
  onUpdate,
  readOnly = false,
}) => {
  const handleUpdateItem = (index: number, updates: Partial<ManualMaterialItem>) => {
    const updatedItems = [...items];
    const item = { ...updatedItems[index], ...updates };
    
    // Recalculate total price
    item.totalPrice = item.quantity * item.unitPrice;
    
    updatedItems[index] = item;
    onUpdate(updatedItems);
  };

  const handleAddItem = (preset?: typeof COMMON_MATERIALS[0]) => {
    const newItem: ManualMaterialItem = {
      id: `material-${Date.now()}`,
      description: preset?.description || '',
      quantity: 0,
      unit: preset?.unit || 'ea',
      unitPrice: preset?.unitPrice || 0,
      totalPrice: 0,
    };
    onUpdate([...items, newItem]);
  };

  const handleDeleteItem = (index: number) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  const getTotal = () => {
    return items.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  // Check which common materials are already added
  const addedDescriptions = new Set(items.map(item => item.description));
  const availablePresets = COMMON_MATERIALS.filter(m => !addedDescriptions.has(m.description));

  return (
    <div className="bucket-editor materials-editor">
      <div className="bucket-header">
        <h4>Additional Materials</h4>
        <div className="bucket-total">{formatCurrency(getTotal())}</div>
      </div>

      <table className="bucket-table">
        <thead>
          <tr>
            <th>Description</th>
            <th>Quantity</th>
            <th>Unit</th>
            <th>Unit Price</th>
            <th>Total</th>
            {!readOnly && <th></th>}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={item.id}>
              <td>
                {readOnly ? (
                  <span>{item.description}</span>
                ) : (
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleUpdateItem(index, { description: e.target.value })}
                    placeholder="Material description"
                  />
                )}
              </td>
              <td>
                {readOnly ? (
                  <span>{item.quantity}</span>
                ) : (
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleUpdateItem(index, { quantity: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="1"
                  />
                )}
              </td>
              <td>
                {readOnly ? (
                  <span>{item.unit}</span>
                ) : (
                  <select
                    value={item.unit}
                    onChange={(e) => handleUpdateItem(index, { unit: e.target.value })}
                  >
                    <option value="ea">ea</option>
                    <option value="lot">lot</option>
                    <option value="lb">lb</option>
                    <option value="box">box</option>
                    <option value="set">set</option>
                  </select>
                )}
              </td>
              <td>
                {readOnly ? (
                  <span>{formatCurrency(item.unitPrice)}</span>
                ) : (
                  <input
                    type="number"
                    value={item.unitPrice}
                    onChange={(e) => handleUpdateItem(index, { unitPrice: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                  />
                )}
              </td>
              <td className="calculated-cell">
                {formatCurrency(item.totalPrice)}
              </td>
              {!readOnly && (
                <td>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteItem(index)}
                    title="Delete item"
                  >
                    ×
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {!readOnly && (
        <div className="materials-actions">
          <button className="add-line-btn" onClick={() => handleAddItem()}>
            + Add Material
          </button>
          
          {availablePresets.length > 0 && (
            <select
              className="quick-add-select"
              onChange={(e) => {
                if (!e.target.value) return;
                const preset = COMMON_MATERIALS.find(m => m.description === e.target.value);
                if (preset) {
                  handleAddItem(preset);
                }
                e.target.value = '';
              }}
            >
              <option value="">Quick Add Common Material...</option>
              {availablePresets.map(material => (
                <option key={material.description} value={material.description}>
                  {material.description} (${material.unitPrice}/{material.unit})
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {items.length === 0 && (
        <div className="materials-note">
          Add fasteners, post bases, pegs, and other materials not in the timber list.
          <br />
          <span className="hint-text">Common items: Timberlinx Post Bases ($80/ea), Pegs ($1.50/ea), Fasteners (varies)</span>
        </div>
      )}
    </div>
  );
};

export default ManualMaterialsEditor;
